import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import { Field, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'
import { fetchDashboard, fetchFunnel } from '@/api/lcCrm'
import {
  ATTENDANCE_STATUS_LABEL,
  HOLD_CLOSE_REASON_LABEL,
  LEAD_PRIORITY_LABEL,
  LEAD_STAGE_LABEL,
  LEAD_STAGE_ORDER,
  SOURCE_TYPE_LABEL,
  formatKstDate,
  formatRate,
  labelOf,
  formatPhone,
} from './crmLabels'
import { ErrorBlock, LoadingBlock, StageBadge } from './crmComponents'

/**
 * 계약 전 대시보드 (설계 §11).
 *
 * 퍼널은 현재 단계 스냅샷이 아니라 기간 cohort의 누적 도달이다. 그래서 "지금 이 단계에 몇 명"과
 * 숫자가 다르다 — 이미 지나간 단계도 도달로 센다.
 */
export function CrmDashboardPage() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [range, setRange] = useState<{ from?: string; to?: string }>({})

  const dashboard = useQuery({
    queryKey: ['lc-crm-dashboard'],
    queryFn: fetchDashboard,
  })

  const funnel = useQuery({
    queryKey: ['lc-crm-funnel', range],
    queryFn: () => fetchFunnel(range),
  })

  if (dashboard.isLoading) return <LoadingBlock />
  if (dashboard.error || !dashboard.data) return <ErrorBlock />

  const data = dashboard.data

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">대시보드</h2>
        <p className="text-sm text-muted-foreground">
          기본 지표는 테스트 연락처를 뺀 값입니다.
        </p>
      </div>

      {/* 대표 KPI는 접수 고객(사람) 단위다 (ADR-0008, 2026-09-15). 접수 건수는 보조 지표로만 둔다. */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="접수 고객"
          value={data.intakeCustomers.total}
          description={`설명회만 ${data.intakeCustomers.briefingOnly.toLocaleString('ko-KR')} · 문의만 ${data.intakeCustomers.franchiseOnly.toLocaleString('ko-KR')} · 둘 다 ${data.intakeCustomers.both.toLocaleString('ko-KR')}${data.intakeCustomers.other > 0 ? ` · 기타 ${data.intakeCustomers.other.toLocaleString('ko-KR')}` : ''}`}
        />
        <MetricCard label="설명회 참석" value={data.attendedCustomers} description="한 번이라도 참석한 접수 고객" />
        <MetricCard label="리드" value={data.totalLeads} />
        <MetricCard label="접수 건수" value={data.totalIntakes} description="설명회 신청·가맹 문의 건 합계" />
        <MetricCard label="진행 중 할 일" value={data.openTasks} />
        <MetricCard
          label="기한 지난 할 일"
          value={data.overdueTaskCount}
          emphasis={data.overdueTaskCount > 0}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>cohort 퍼널</CardTitle>
          <CardDescription>
            기간 안에 리드로 전환된 사람들이 각 단계를 거쳐 갔는지를 셉니다. 기간은 KST 업무일이고
            종료일을 포함합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field className="w-44">
              <FieldLabel htmlFor="funnel-from">시작일</FieldLabel>
              <Input id="funnel-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field className="w-44">
              <FieldLabel htmlFor="funnel-to">종료일 (포함)</FieldLabel>
              <Input id="funnel-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Button
              className="mb-0.5"
              variant="outline"
              onClick={() => setRange({ from: from || undefined, to: to || undefined })}
            >
              적용
            </Button>
            <Button
              className="mb-0.5"
              variant="ghost"
              onClick={() => {
                setFrom('')
                setTo('')
                setRange({})
              }}
            >
              전체 기간
            </Button>
          </div>

          {funnel.isLoading && <LoadingBlock />}
          {funnel.error && <ErrorBlock message="퍼널을 불러오지 못했습니다." />}

          {funnel.data && (
            <>
              <p className="text-sm text-muted-foreground">
                cohort {funnel.data.cohortSize.toLocaleString('ko-KR')}명
                {funnel.data.from || funnel.data.to
                  ? ` · ${funnel.data.from ?? '처음'} ~ ${funnel.data.to ?? '지금'}`
                  : ' · 전체 기간'}
              </p>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>단계</TableHead>
                    <TableHead className="text-right">도달</TableHead>
                    <TableHead className="text-right">평균 체류일</TableHead>
                    <TableHead className="text-right">직전 단계 대비</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {funnel.data.stages.map((stage, index) => {
                    const conversion = funnel.data!.conversions.find(
                      (item) => item.toStage === stage.stage,
                    )
                    return (
                      <TableRow key={stage.stage}>
                        <TableCell><StageBadge stage={stage.stage} /></TableCell>
                        <TableCell className="text-right tabular-nums">
                          {stage.reachedCount.toLocaleString('ko-KR')}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {stage.averageDaysInStage == null
                            ? '-'
                            : `${stage.averageDaysInStage.toFixed(1)}일`}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {index === 0 || !conversion ? (
                            '-'
                          ) : (
                            <span className="flex items-center justify-end gap-1.5">
                              {formatRate(conversion.rate)}
                              <span className="text-xs text-muted-foreground">
                                ({conversion.numerator}/{conversion.denominator})
                              </span>
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              <p className="text-xs text-muted-foreground">
                리드 단계의 도달은 누적입니다. 앞 단계를 건너뛰고 뒤 단계로 간 리드는 앞 단계도 지난 것으로 셉니다.
                신청·설명회 참석은 접수함에 기록된 실제 신청자·참석자 수이며, 참석 → 신규 리드 비율은 참석자 중
                리드가 된 사람으로 셉니다.
              </p>

              <div className="grid gap-5 lg:grid-cols-2">
                <DistributionBlock
                  title="현재 단계 분포"
                  description="cohort 리드가 지금 어느 단계에 있는지 (보류 · 종결 포함)"
                  labels={LEAD_STAGE_LABEL}
                  values={funnel.data.currentStageDistribution}
                  order={CURRENT_STAGE_ORDER}
                />
                <DistributionBlock
                  title="이탈 사유"
                  description={`보류 · 종결 ${funnel.data.holdClosedCount}건`}
                  labels={HOLD_CLOSE_REASON_LABEL}
                  values={funnel.data.holdCloseReasonDistribution}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>분포</CardTitle>
            <CardDescription>진행 중인 리드 기준입니다.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <DistributionBlock title="단계" labels={LEAD_STAGE_LABEL} values={data.stageDistribution} />
            <DistributionBlock title="우선순위" labels={LEAD_PRIORITY_LABEL} values={data.priorityDistribution} />
            <DistributionBlock title="설명회 참석" labels={ATTENDANCE_STATUS_LABEL} values={data.attendanceDistribution} />
            <DistributionBlock title="희망 개점 시기" labels={{}} values={data.desiredOpenMonthDistribution} />
            <DistributionBlock title="희망 지역" labels={{}} values={data.desiredRegionDistribution} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>최근 접수</CardTitle>
            <CardDescription>접수함으로 이동해 리드 전환을 진행합니다.</CardDescription>
            <Button className="justify-self-end" size="sm" variant="outline" render={<Link to="/lc-crm/inbox" />}>
              접수함
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>이름</TableHead>
                  <TableHead>연락처</TableHead>
                  <TableHead>유형</TableHead>
                  <TableHead>접수일</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recentIntakes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                      최근 접수가 없습니다
                    </TableCell>
                  </TableRow>
                )}
                {data.recentIntakes.map((intake) => (
                  <TableRow key={intake.intakeId}>
                    <TableCell>{intake.contactName ?? '-'}</TableCell>
                    <TableCell className="tabular-nums">{formatPhone(intake.phoneNormalized)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{labelOf(SOURCE_TYPE_LABEL, intake.sourceType)}</Badge>
                    </TableCell>
                    <TableCell className="tabular-nums">{formatKstDate(intake.receivedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  description,
  emphasis,
}: {
  label: string
  value: number
  description?: string
  emphasis?: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className={emphasis ? 'text-2xl text-destructive tabular-nums' : 'text-2xl tabular-nums'}>
          {value.toLocaleString('ko-KR')}
        </CardTitle>
      </CardHeader>
      {description && (
        <CardContent>
          <p className="text-xs text-muted-foreground">{description}</p>
        </CardContent>
      )}
    </Card>
  )
}

/** 현재 단계 분포는 파이프라인 순서로 보여준다. 보류·종결은 진행 축 밖이라 맨 뒤다. */
const CURRENT_STAGE_ORDER: readonly string[] = [...LEAD_STAGE_ORDER, 'HOLD_CLOSED']

/**
 * 막대 대신 비율 바 하나. 값이 0인 항목도 지우지 않는다 — 없는 것과 0은 다르다.
 * `order`를 주면 그 순서(없는 키는 0으로 채움)로, 아니면 큰 값부터 보여준다.
 */
function DistributionBlock({
  title,
  description,
  labels,
  values,
  order,
}: {
  title: string
  description?: string
  labels: Record<string, string>
  values: Record<string, number>
  order?: readonly string[]
}) {
  const entries = order
    ? [
        ...order.map((key): [string, number] => [key, values[key] ?? 0]),
        ...Object.entries(values).filter(([key]) => !order.includes(key)),
      ]
    : Object.entries(values).sort((a, b) => b[1] - a[1])
  const max = entries.reduce((acc, [, count]) => Math.max(acc, count), 0)

  return (
    <section className="flex flex-col gap-2">
      <div>
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      {entries.length === 0 && <p className="text-sm text-muted-foreground">데이터가 없습니다.</p>}
      {entries.map(([key, count]) => (
        <div key={key} className="flex items-center gap-3">
          <span className="w-32 shrink-0 truncate text-sm">{labelOf(labels, key)}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: max > 0 ? `${(count / max) * 100}%` : '0%' }}
            />
          </div>
          <span className="w-12 shrink-0 text-right text-sm tabular-nums">
            {count.toLocaleString('ko-KR')}
          </span>
        </div>
      ))}
    </section>
  )
}
